import React, { useState, useMemo, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { useProducts, useDebounce } from './hooks/useProducts';
import {
    Header,
    Footer,
    StatsSection,
    ProductGrid,
    ProductModal
} from './components';
import AdminLogin from './pages/AdminLogin';
import AdminDownloads from './pages/AdminDownloads';
import Home from './pages/Home';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

/**
 * ============================================
 * App Component - Main Application
 * ============================================
 * Stock management application for curtains (rideaux)
 */

function MainApp() {
    // State
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    
    const scrollContainerRef = useRef(null);

    // GSAP Smooth Scrolling Setup
    useEffect(() => {
        // Apply CSS smooth scrolling
        document.documentElement.style.scrollBehavior = 'auto';
        
        let scrollVelocity = 0;
        let currentY = window.pageYOffset;
        let targetY = window.pageYOffset;
        let isScrolling = false;
        
        // Smooth scroll animation with momentum
        const updateScroll = () => {
            if (Math.abs(targetY - currentY) > 0.5) {
                currentY += (targetY - currentY) * 0.08; // Smooth easing factor
                window.scrollTo(0, currentY);
                isScrolling = true;
            } else {
                currentY = targetY;
                isScrolling = false;
            }
            requestAnimationFrame(updateScroll);
        };
        
        // Handle wheel events for smooth scrolling
        const handleWheel = (e) => {
            e.preventDefault();
            const delta = e.deltaY;
            targetY += delta;
            targetY = Math.max(0, Math.min(targetY, document.documentElement.scrollHeight - window.innerHeight));
        };
        
        // Handle anchor clicks
        const handleAnchorClick = (e) => {
            const target = e.target.closest('a[href^="#"]');
            if (target) {
                e.preventDefault();
                const id = target.getAttribute('href').slice(1);
                const element = document.getElementById(id);
                if (element) {
                    targetY = element.offsetTop - 80;
                }
            }
        };
        
        window.addEventListener('wheel', handleWheel, { passive: false });
        document.addEventListener('click', handleAnchorClick);
        
        updateScroll();

        return () => {
            window.removeEventListener('wheel', handleWheel);
            document.removeEventListener('click', handleAnchorClick);
            document.documentElement.style.scrollBehavior = 'smooth';
            ScrollTrigger.getAll().forEach(trigger => trigger.kill());
        };
    }, []);

    // Debounced search term for API calls
    const debouncedSearch = useDebounce(searchTerm, 300);

    // Reset page when search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearch]);

    // Fetch products with filters and pagination
    const { products, loading: isLoading, error, pagination } = useProducts({
        search: debouncedSearch,
        page: currentPage,
        limit: 10
    });

    // Calculate stats
    const stats = useMemo(() => {
        if (!products || products.length === 0) {
            return {
                totalProducts: 0,
                outOfStockCount: 0
            };
        }

        let outOfStockCount = 0;

        products.forEach(product => {
            if (product.isOutOfStock) {
                outOfStockCount++;
            }
        });

        return {
            totalProducts: products.length,
            outOfStockCount
        };
    }, [products]);

    // Handle product click - open modal
    const handleProductClick = (product) => {
        setSelectedProduct(product);
        setIsModalOpen(true);
    };

    // Handle modal close
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setTimeout(() => setSelectedProduct(null), 300);
    };

    // Reset filters
    const handleResetFilters = () => {
        setSearchTerm('');
        setCurrentPage(1);
    };

    // Pagination handlers
    const handlePageChange = (page) => {
        setCurrentPage(page);
        // Scroll to top of products
        const productsSection = document.getElementById('products');
        if (productsSection) {
            productsSection.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <div ref={scrollContainerRef} className="min-h-screen flex flex-col bg-gray-50">
            {/* Header */}
            <Header
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
            />

            {/* Main Content */}
            <main className="flex-1">
                {/* Simple Catalog Header */}
                <div id="products" className="w-full py-12 bg-white border-b border-gray-200 mt-20">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <h1 className="text-5xl md:text-6xl font-bold text-[#3D3D3D] mb-4 tracking-tight font-serif">
                            Notre Collection Premium
                        </h1>
                        <div className="flex items-center justify-center gap-8 text-sm uppercase tracking-widest text-gray-600 font-semibold">
                            <button className="hover:text-[#3D3D3D] transition-colors">
                                PAR GAMME
                            </button>
                            <span>·</span>
                            <button className="hover:text-[#3D3D3D] transition-colors">
                                PAR STYLE
                            </button>
                            <span>·</span>
                            <button className="hover:text-[#3D3D3D] transition-colors">
                                PAR PIÈCE
                            </button>
                        </div>
                    </div>
                </div>

                {/* Stats Section */}
                <StatsSection
                    totalProducts={stats.totalProducts}
                    outOfStockCount={stats.outOfStockCount}
                />

                {/* Error Message */}
                {error && (
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                        <div className="glass-light border-2 border-red-500/40 rounded-2xl p-5 text-red-400 text-sm font-semibold">
                            <strong className="font-bold">Error:</strong> {error}. Showing demonstration data.
                        </div>
                    </div>
                )}

                {/* Products Grid */}
                <ProductGrid
                    products={products}
                    isLoading={isLoading}
                    searchTerm={searchTerm}
                    onProductClick={handleProductClick}
                    onResetFilters={handleResetFilters}
                    pagination={pagination}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                />
            </main>

            {/* Footer */}
            <Footer />


            {/* Product Modal */}
            <ProductModal
                product={selectedProduct}
                isOpen={isModalOpen}
                onClose={handleCloseModal}
            />
        </div>
    );
}

function App() {
    return (
        <Router>
            <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/catalog" element={<MainApp />} />
                <Route path="/admin" element={<AdminLogin />} />
                <Route path="/admin/downloads" element={<AdminDownloads />} />
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </Router>
    );
}

export default App;
